
// src/components/EndUser/S4ReasonTable.tsx
import React from 'react';
import {
  Stack, Typography,
  Table, TableHead, TableRow, TableCell, TableBody,
  FormControl, InputLabel, Select, MenuItem, TextField, FormHelperText, Button,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useArrayData } from '../../context/ArrayDataContext';
import { formControlStyle } from '../../utils/theme';
type Choice = 'IO' | 'S4';
type ReasonOption = { value: string; label?: string };

type S4ReasonTableProps = {
  /** rows to show in the table */
  selectedRows: any[];
  /** called after successful submit with the built payloads */
  onSubmit?: (payloads: any[]) => void;
  /** called when user cancels */
  onCancel?: () => void;
  /** optional reason list */
  reasonOptions?: ReasonOption[];

  /** NEW: control dialog visibility */
  open: boolean;
  /** NEW: close handler for the dialog (used by Cancel/X) */
  onClose: () => void;
};

// ---- Utils (unchanged) ----
const toFieldName = (key: string) => {
  if (!key) return key as any;
  const parts = key.split(' ');
  parts[0] = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
  return parts.join('').replace(/[^\w]/g, '');
};

const toSafeString = (v: any) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '';
  return String(v).trim();
};

const createPayloadFromChoice = (
  item: any,
  choice: Choice,
  idx?: number,
  overrides?: { reason?: string; comments?: string }
) => {
  const idSafe =
    String(
      item?.materialNumber ??
      item?.['Material Number'] ??
      item?.id ??
      item?.Id ??
      item?.ID ??
      ''
    ).trim() || `tmp-${Date.now()}-${idx ?? 0}`;

  let submitSafetyStock = '';
  let submitCoverage = '';
  let submitSafetyTime = '';

  if (choice === 'S4') {
    submitSafetyStock = toSafeString(item?.['S4 Safety Stock (Qty)'] ?? item?.['s4SafetyStock(Qty)']);
    submitCoverage    = toSafeString(item?.['S4 Coverage Profile (wk)'] ?? item?.['s4CoverageProfile(wk)']);
    submitSafetyTime  = toSafeString(item?.['S4 Safety Time (wk)'] ?? item?.['s4SafetyTime(wk)']);
  } else {
    submitSafetyStock = toSafeString(item?.['IO Consolidated Safety Stock (wk) - Rounded'] ?? item?.['iOConsolidatedSafetyStock(wk)-Rounded']);
    submitCoverage    = toSafeString(item?.['IO Coverage (Weeks) (Rounded)'] ?? item?.['iOCoverage(Weeks)(Rounded)']);
    submitSafetyTime  = toSafeString(item?.['IO Safety Time (Weeks) (Rounded)'] ?? item?.['iOSafetyTime(Weeks)(Rounded)']);
  }

  const reasonVal   = toSafeString(overrides?.reason   ?? item?.['Reason']   ?? item?.reasonCode ?? '');
  const commentsVal = toSafeString(overrides?.comments ?? item?.['Comments'] ?? item?.comments  ?? '');

  const payload: any = {
    ...item,
    id: idSafe,
    materialNumber: item?.materialNumber ?? item?.['Material Number'] ?? idSafe,
    ['Safety Stock (Qty) - End User Proposal']: submitSafetyStock,
    ['Coverage Profile (wk) - End User Proposal']: submitCoverage,
    ['Safety Time (wk) - End User Proposal']: submitSafetyTime,
    [toFieldName('Safety Stock (Qty) - End User Proposal')]: submitSafetyStock,
    [toFieldName('Coverage Profile (wk) - End User Proposal')]: submitCoverage,
    [toFieldName('Safety Time (wk) - End User Proposal')]: submitSafetyTime,
    ['End User action']: choice,
    [toFieldName('End User action')]: choice,
    ['Reason']: reasonVal,
    ['Comments']: commentsVal,
    [toFieldName('Reason')]: reasonVal,
    [toFieldName('Comments')]: commentsVal,
    lineStatus: '2. Proposal Pending Approval'
  };

  return payload;
};

const S4ReasonTable: React.FC<S4ReasonTableProps> = ({
  selectedRows,
  onSubmit,
  onCancel,
  reasonOptions = [
    { value: 'Maintain current S4 due to planning judgement' },
    { value: 'IO appears outlier / data quality concern' },
    { value: 'Stock risk if IO applied' },
    { value: 'Lead time / variability change' },
    { value: 'Other' },
  ],
  /** NEW: dialog control */
  open,
  onClose,
}) => {
  const { upsertItem } = useArrayData();

  const getKey = (r: any, idx: number) =>
    String(r?.materialNumber ?? r?.['Material Number'] ?? r?.id ?? idx).trim();

  const [perRowInputs, setPerRowInputs] = React.useState<Record<
    string, { reason: string; comments: string; error?: boolean }
  >>({});

  // initialize per-row inputs when dialog opens/rows change
  React.useEffect(() => {
    const next: Record<string, { reason: string; comments: string }> = {};
    (selectedRows ?? []).forEach((r, idx) => {
      const k = getKey(r, idx);
      next[k] = {
        reason: toSafeString(r?.['Reason'] ?? r?.reasonCode ?? ''),
        comments: toSafeString(r?.['Comments'] ?? r?.comments ?? ''),
      };
    });
    setPerRowInputs(next);
  }, [selectedRows, open]);

  const handleReasonChange = (key: string, value: string) => {
    setPerRowInputs(prev => ({ ...prev, [key]: { ...prev[key], reason: value, error: !value } }));
  };

  const handleCommentsChange = (key: string, value: string) => {
    setPerRowInputs(prev => ({ ...prev, [key]: { ...prev[key], comments: value } }));
  };

  // validation: every row must have a reason
  const allReasonsSelected = (selectedRows ?? []).every((r, idx) => !!perRowInputs[getKey(r, idx)]?.reason);

  const submitS4 = () => {
    if (!allReasonsSelected) {
      const next = { ...perRowInputs };
      (selectedRows ?? []).forEach((r, idx) => {
        const k = getKey(r, idx);
        if (!next[k]?.reason) next[k] = { ...(next[k] ?? { reason: '', comments: '' }), error: true };
      });
      setPerRowInputs(next);
      return;
    }

    // Build payloads with choice=S4
    const payloads = (selectedRows ?? [])
      .filter(r => !!r)
      .map((r, idx) => {
        const k = getKey(r, idx);
        const overrides = perRowInputs[k] ?? { reason: '', comments: '' };
        return createPayloadFromChoice(r, 'S4', idx, overrides);
      });

    // Upsert into shared context & persist to sessionStorage
    payloads.forEach(payload => upsertItem(payload));

    try {
      const existing = sessionStorage.getItem('approverRows');
      const prior = existing ? JSON.parse(existing) : [];
      const byId = new Map<string, any>();
      [...prior, ...payloads].forEach(row => {
        const key = String(row?.id ?? row?.materialNumber ?? row?.['Material Number'] ?? '').trim();
        byId.set(key || `${Date.now()}-${Math.random()}`, row);
      });
      sessionStorage.setItem('approverRows', JSON.stringify(Array.from(byId.values())));
    } catch { /* ignore storage errors */ }

    onSubmit?.(payloads);
    onClose();           // close dialog after successful submit
  };

  const cancel = () => {
    onCancel?.();
    onClose();           // close dialog on cancel
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ "& .MuiDialog-paper": { backgroundColor: "#ddd" } }}
    >
      <DialogTitle>Provide Reason & Comments (S4)</DialogTitle>

      <DialogContent>
        <Stack gap={1.5}>
          <Typography variant="subtitle2">
            Provide <b>Reason</b> (required) and <b>Comments</b> (optional) for selected rows:
          </Typography>

          <Table
            size="small"
            sx={{
              borderCollapse: 'collapse',
              border: '1px solid #333',
              borderRadius: '8px',
              backgroundColor: '#ddd'
            }}
          >
            <TableHead>
              <TableRow sx={{ borderBottom: '2px solid #333' }}>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #333', color: '#000', padding: '8px' }}>
                  Material
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #333', color: '#000', padding: '8px' }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #333', color: '#000', padding: '8px' }}>
                  Reason *
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #333', color: '#000', padding: '8px' }}>
                  Comments
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedRows ?? []).map((r, idx) => {
                const key = getKey(r, idx);
                const values = perRowInputs[key] ?? { reason: '', comments: '' };
                const reasonError = !values.reason && values.error;
                return (
                  <TableRow key={key} sx={{ borderBottom: '1px solid #333', backgroundColor: '#ddd' }}>
                    <TableCell sx={{ padding: '8px', borderRight: '1px solid #333', color: '#000', borderBottom: '1px solid #333' }}>
                      {String(r?.materialNumber ?? r?.['Material Number'] ?? '')}
                    </TableCell>
                    <TableCell sx={{ padding: '8px', borderRight: '1px solid #333', color: '#000', borderBottom: '1px solid #333' }}>
                      {String(r?.materialDescription ?? r?.['Material Description'] ?? '')}
                    </TableCell>
                    <TableCell sx={{ padding: '8px', borderRight: '1px solid #333', color: '#000', borderBottom: '1px solid #333' }}>
                      <FormControl fullWidth size="small" error={reasonError} sx={formControlStyle}>
                        <InputLabel id={`reason-label-${key}`} sx={{ fontSize: '14px', color: '#000', width: '200px' }}>
                          Reason
                        </InputLabel>
                        <Select
                          labelId={`reason-label-${key}`}
                          label="Reason"
                          value={values.reason}
                          onChange={(e) => handleReasonChange(key, String(e.target.value))}
                          MenuProps={{
                            PaperProps: { style: { maxHeight: '200px', width: '150px', overflowY: 'auto' } },
                          }}
                          sx={{
                            fontSize: '14px',
                            height: '35px',
                            width: '250px',
                            color: '#000',
                            '& .MuiSelect-icon': { color: '#000' },
                          }}
                        >
                          {reasonOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                              {opt.label ?? opt.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {reasonError && <FormHelperText>Reason is required</FormHelperText>}
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ padding: '8px', color: '#000', borderBottom: '1px solid #333' }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Optional comments"
                        value={values.comments}
                        onChange={(e) => handleCommentsChange(key, e.target.value)}
                        sx={{
                          fontSize: '14px',
                          height: '35px',
                          backgroundColor: '#ddd',
                          color: '#000',
                          '&::placeholder': { color: '#888' },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          color="error"
          variant="outlined"
          sx={{ borderRadius: "50px", backgroundColor: "#000", color: "#fff", "&:hover": { backgroundColor: "red", color: "#fff" } }}
          onClick={cancel}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          sx={{ borderRadius: "50px", border: "0.74px solid green", color: "#000", "&:hover": { backgroundColor: "green", color: "#fff" } }}
          onClick={submitS4}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default S4ReasonTable;
